import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { StaticPageHeader } from '../components/StaticPageHeader';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Download,
  FileText,
  Mail,
  Search,
  ShoppingBag,
} from 'lucide-react';
import { formatApiErrorDetail } from '../lib/apiError';
import { track as pixelTrack } from '../lib/metaPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  'hiring-onboarding': 'Hiring & Onboarding',
  'hr-policies': 'HR Policies',
  'performance-development': 'Performance & Development',
  'benefits-compensation': 'Benefits & Compensation',
  'termination-offboarding': 'Termination & Offboarding',
  'compliance-legal': 'Compliance & Legal',
  'culture-dei-engagement': 'Culture, DEI & Engagement',
  'hr-operations': 'HR Operations',
};

const formatKb = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [dlState, setDlState] = useState({}); // tmpl_id -> 'loading'|'done'|'error'
  const pollAttempts = useRef(0);
  const cancelledRef = useRef(false);
  // Fire the Purchase pixel event exactly once per session.
  const trackedRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!sessionId) {
      setError('Missing session ID — open this page from your purchase confirmation link.');
      setPolling(false);
      return () => {};
    }
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/bundles/purchase/${sessionId}`);
        if (cancelledRef.current) return;
        setStatus(data);
        if (data.payment_status === 'paid') {
          setPolling(false);
          if (!trackedRef.current) {
            pixelTrack('Purchase', {
              value: Number(data.amount) || 0,
              currency: 'USD',
              content_name: data.product_name || 'DriveHR Bundle',
              content_type: 'product',
              content_ids: [sessionId],
            });
            trackedRef.current = true;
          }
          return;
        }
        if (data.payment_status === 'failed') {
          setError('This payment did not complete. If you were charged, contact help@drive-hr.com.');
          setPolling(false);
          return;
        }
        pollAttempts.current += 1;
        if (pollAttempts.current >= 20) {
          setPolling(false);
          setError("It's taking longer than expected to confirm your payment. Refresh in a minute, or email help@drive-hr.com.");
          return;
        }
        setTimeout(tick, 1500);
      } catch (e) {
        if (cancelledRef.current) return;
        const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
        setError(detail);
        setPolling(false);
      }
    };
    tick();
    return () => { cancelledRef.current = true; };
  }, [sessionId]);

  const downloadOne = async (t) => {
    setDlState((s) => ({ ...s, [t.id]: 'loading' }));
    try {
      const resp = await axios.get(
        `${API}/bundles/purchase/${sessionId}/templates/${t.id}/download`,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: t.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = t.file_name || t.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDlState((s) => ({ ...s, [t.id]: 'done' }));
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setDlState((s) => ({ ...s, [t.id]: 'error' }));
      alert(msg);
    }
  };

  const [bulkDl, setBulkDl] = useState('idle'); // idle | loading | done | error
  const downloadAll = async () => {
    if (!status?.templates?.length) return;
    setBulkDl('loading');
    try {
      const resp = await axios.get(
        `${API}/bundles/purchase/${sessionId}/download-all`,
        { responseType: 'blob' },
      );
      // Pull the filename out of the Content-Disposition header if present,
      // falling back to a slugified product name client-side.
      const cd = resp.headers['content-disposition'] || resp.headers['Content-Disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(cd);
      const slug = (status?.product_name || 'DriveHR-Bundle').replace(/&/g, 'and').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
      const filename = match ? match[1] : `${slug || 'DriveHR-Bundle'}.zip`;
      const blob = new Blob([resp.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setBulkDl('done');
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setBulkDl('error');
      alert(`Couldn't bundle your downloads — ${msg}`);
    }
  };

  const filteredTemplates = (status?.templates || []).filter((t) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      (CATEGORY_LABELS[t.category] || t.category || '').toLowerCase().includes(q)
    );
  });

  if (!sessionId || (error && !status)) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="payment-success-error">
        <StaticPageHeader />
        <main className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">We couldn't load your order</h1>
            <p className="text-gray-600 mt-3">{error || 'Missing session ID.'}</p>
            <Link to="/" className="mt-6 inline-block text-blue-600 font-semibold">← Back to home</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (polling && (!status || status.payment_status !== 'paid')) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="payment-success-polling">
        <StaticPageHeader />
        <main className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
            <Loader2 className="w-12 h-12 text-blue-600 mx-auto animate-spin mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Confirming your payment...</h1>
            <p className="text-gray-600 mt-3">This usually takes just a few seconds. Don't close this tab.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const paid = status?.payment_status === 'paid';
  const isService = status?.is_service;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="payment-success-page">
      <StaticPageHeader />
      <main className="flex-1 pt-28 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-6" data-testid="purchase-header">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-7 h-7 text-green-600" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {isService ? 'Order confirmed' : 'Your downloads are ready'}
                </h1>
                <p className="text-gray-600 mt-1" data-testid="purchase-product-name">
                  Thank you for purchasing <span className="font-semibold text-gray-900">{status?.product_name}</span>.
                </p>
                <div className="mt-4 flex flex-wrap gap-3 text-sm">
                  <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                    Paid · ${status?.amount?.toFixed(2)} USD
                  </span>
                  {status?.customer_email && (
                    <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 inline-flex items-center gap-1" data-testid="purchase-email">
                      <Mail className="w-3.5 h-3.5" />
                      {status.customer_email}
                    </span>
                  )}
                  {!isService && (
                    <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-700 font-medium" data-testid="purchase-template-count">
                      {status?.template_count} template{status?.template_count === 1 ? '' : 's'} included
                    </span>
                  )}
                </div>
                {status?.customer_email && (
                  <p className="text-xs text-gray-500 mt-4">
                    We've also emailed a copy of this download link to <strong>{status.customer_email}</strong> — bookmark it for re-access from any device.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Service product (handbook-builder) — no files to download */}
          {paid && isService && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center" data-testid="purchase-service-card">
              <ShoppingBag className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900">Your service order is being processed</h2>
              <p className="text-gray-600 mt-3 max-w-md mx-auto">
                Our team will reach out to <strong>{status?.customer_email}</strong> within 1–2 business days to kick things off.
              </p>
              <Link to="/" className="mt-6 inline-block text-blue-600 font-semibold">← Back to home</Link>
            </div>
          )}

          {/* Files grid */}
          {paid && !isService && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Downloads</h2>
                  <p className="text-sm text-gray-500">Click any template to download. Files are editable Word / Excel / PDF.</p>
                </div>
                <Button onClick={downloadAll} disabled={bulkDl === 'loading'} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="download-all-btn">
                  {bulkDl === 'loading'
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing .zip...</>
                    : bulkDl === 'done'
                      ? <><CheckCircle2 className="w-4 h-4 mr-2 text-white" />Downloaded .zip</>
                      : <><Download className="w-4 h-4 mr-2" />Download all as .zip ({status?.templates?.length})</>}
                </Button>
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter templates by name, description, or category..."
                  className="pl-10"
                  data-testid="purchase-search-input"
                />
              </div>

              {filteredTemplates.length === 0 ? (
                <div className="text-center py-12 text-gray-500" data-testid="purchase-empty">
                  {status?.templates?.length === 0
                    ? 'This bundle has no templates available yet — contact help@drive-hr.com and we\'ll get them to you directly.'
                    : 'No templates match your filter.'}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="purchase-templates-grid">
                  {filteredTemplates.map((t) => {
                    const dl = dlState[t.id];
                    return (
                      <div
                        key={t.id}
                        className="bg-gray-50 hover:bg-blue-50 hover:border-blue-300 transition rounded-xl p-4 border border-gray-200 flex items-start gap-3"
                        data-testid={`purchase-template-${t.id}`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate" title={t.name}>{t.name}</h3>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                            <span>{CATEGORY_LABELS[t.category] || t.category}</span>
                            {t.file_size > 0 && <><span>·</span><span>{formatKb(t.file_size)}</span></>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={dl === 'loading'}
                          onClick={() => downloadOne(t)}
                          data-testid={`download-template-${t.id}`}
                        >
                          {dl === 'loading'
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : dl === 'done'
                              ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                              : <Download className="w-4 h-4" />}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-xs text-gray-500 mt-6 text-center">
                Bookmark this page — your download link will keep working any time you need to re-grab a file.
                <br />
                Want unlimited access to all 245 templates? <Link to="/#memberships" className="text-blue-600 hover:underline">View memberships →</Link>
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const PaymentCanceledPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <StaticPageHeader />
      <main className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="payment-canceled-page">
          <h1 className="text-2xl font-bold text-gray-900">Checkout canceled</h1>
          <p className="text-gray-600 mt-3">No worries — you weren't charged. Pick a bundle whenever you're ready.</p>
          <div className="mt-6 space-y-3">
            <Button onClick={() => navigate(-1)} className="w-full bg-blue-600 hover:bg-blue-700 text-white">Back to the bundle</Button>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">Home</Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
